import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from './firebase';
import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';
import { LogIn } from 'lucide-react';

export default function Login({ onLogin }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loginMode, setLoginMode] = useState('email'); // 'email' | 'username'
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      let loginEmail = email;

      // If username login, resolve to email via BMS Cloud Function
      if (loginMode === 'username' && username) {
        const resolveEmail = httpsCallable(functions, 'shifts_resolveAuthEmail');
        const result = await resolveEmail({ username });
        if (result.data.matches) {
          // Multiple companies — for now use the first match
          // TODO: add company disambiguation UI
          loginEmail = result.data.matches[0].email;
        } else {
          loginEmail = result.data.email;
        }
      }

      const cred = await signInWithEmailAndPassword(auth, loginEmail, password);
      if (onLogin) onLogin(cred.user);
      navigate('/dashboard');
    } catch (err) {
      console.error('Login error:', err);
      if (err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        setError(t('auth.invalidCredentials', 'Invalid email/username or password.'));
      } else {
        setError(err.message || t('auth.loginFailed', 'Login failed.'));
      }
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-2">
            <img src="/logo.png" alt="BuildMyShifts" className="w-12 h-12 object-contain" />
            <h1 className="text-3xl font-bold text-gray-900">
              Build My <span className="text-primary">Shifts</span>
            </h1>
          </div>
          <p className="text-gray-500">{t('app.tagline')}</p>
        </div>

        {/* Login card */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <h2 className="text-xl font-bold text-gray-900 mb-6">{t('auth.login')}</h2>

          {/* Login mode toggle */}
          <div className="flex rounded-lg bg-gray-100 p-1 mb-6">
            <button
              onClick={() => setLoginMode('email')}
              className={`flex-1 py-2 text-sm font-medium rounded-md transition-colors ${
                loginMode === 'email' ? 'bg-white shadow text-primary' : 'text-gray-600'
              }`}
            >
              {t('auth.email')}
            </button>
            <button
              onClick={() => setLoginMode('username')}
              className={`flex-1 py-2 text-sm font-medium rounded-md transition-colors ${
                loginMode === 'username' ? 'bg-white shadow text-primary' : 'text-gray-600'
              }`}
            >
              {t('auth.username')}
            </button>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            {loginMode === 'email' ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('auth.email')}</label>
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-primary/50 focus:border-primary"
                  required
                />
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('auth.username')}</label>
                <input
                  type="text"
                  value={username}
                  onChange={e => setUsername(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-primary/50 focus:border-primary"
                  required
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('auth.password')}</label>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-primary/50 focus:border-primary"
                required
              />
            </div>

            {error && (
              <div className="bg-red-50 text-red-600 text-sm p-3 rounded-lg">{error}</div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-primary text-white rounded-lg py-3 font-semibold hover:bg-primary-dark transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <LogIn size={18} />
              {loading ? t('common.loading') : t('auth.login')}
            </button>
          </form>

          <div className="mt-4 text-center space-y-2">
            <Link to="/forgot-password" className="text-sm text-primary hover:underline">{t('auth.forgotPassword')}</Link>
            <p className="text-sm text-gray-600">
              {t('auth.noAccount')}{' '}
              <Link to="/register" className="text-primary font-medium hover:underline">{t('auth.register')}</Link>
            </p>
          </div>

          {/* Cross-product login hint */}
          <div className="mt-6 pt-4 border-t border-gray-100 text-center">
            <div className="flex items-center justify-center gap-2">
              <img src="/bmc-logo.png" alt="BuildMyChecks" className="w-6 h-6 object-contain rounded" />
              <p className="text-xs text-gray-400">{t('auth.loginWithBMC')}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
